import { useEffect, useState } from "react";
import DocumentSnipPreview from "./DocumentSnipPreview";
import api, { downloadFile, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, AlertTriangle } from "lucide-react";

const NODE_STYLE = {
  complete: "bg-green-100 text-green-700 border-green-300",
  reviewed: "bg-emerald-100 text-emerald-700 border-emerald-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  missing: "bg-red-100 text-red-700 border-red-300",
  exception: "bg-orange-100 text-orange-700 border-orange-300",
};
const TYPE_LABEL = { snippet: "Cuplikan", risk: "Risiko", procedure: "Prosedur", wp: "KKA", evidence: "Bukti", exception: "Eksepsi", conclusion: "Kesimpulan", aje: "AJE", component: "Komponen", figure: "Angka", intercompany: "Intercompany" };

export default function Lineage({ id }) {
  const [d, setD] = useState(null);
  const [gl, setGl] = useState(null);
  const [sel, setSel] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let current = true;
    setD(null); setGl(null); setSel(null); setError("");
    api.get(`/engagements/${id}/lineage`).then((r) => { if (current) setD(r.data); }).catch(e => { if (current) { setD(null); setSel(null); setError(getErrorMessage(e)); } });
    api.get(`/engagements/${id}/group-lineage`).then((r) => { if (current) setGl(r.data); }).catch(() => { if (current) setGl(null); });
    return () => { current = false; };
  }, [id]);
  if (error) return <p role="alert" className="p-4 text-sm">{error}</p>;
  if (!d) return <div className="text-sm text-muted-foreground p-4">Memuat lineage…</div>;

  return (
    <div className="space-y-3" data-testid="lineage-view">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-5 gap-2 flex-1 mr-3">
          {[["Risiko", d.counts.risks], ["Prosedur", d.counts.procedures], ["KKA", d.counts.ewps], ["Bukti", d.counts.evidence], ["Gap Lineage", d.counts.gaps]].map(([l, v], i) => (
            <div key={l} className={`bg-white border rounded-sm p-2 ${i === 4 && v > 0 ? "border-red-300" : "border-border"}`}>
              <div className={`text-base font-head font-bold tabular ${i === 4 && v > 0 ? "text-red-600" : ""}`}>{v}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={() => downloadFile(`/engagements/${id}/lineage/export`, "Lineage_Bukti.xlsx")} data-testid="lineage-export"><Download size={13} className="mr-1" />Ekspor Indeks Lineage</Button>
      </div>

      {d.gaps.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-sm p-3" data-testid="lineage-gaps">
          <div className="font-head font-semibold text-sm text-red-700 flex items-center gap-1 mb-2"><AlertTriangle size={14} />LINEAGE GAP — PERLU REVIU ({d.gaps.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {d.gaps.map((g, i) => (
              <div key={i} className="text-[11px] flex items-center gap-2" data-testid={`gap-${i}`}>
                <span className={`px-1.5 py-0.5 border rounded-sm ${g.significant ? "bg-red-100 text-red-700 border-red-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>{g.type}</span>
                <span className="text-muted-foreground truncate">{g.ref} {g.detail && `· ${g.detail}`}</span>
                {g.significant && <span className="text-[9px] text-red-600">MATERIAL</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs font-head font-semibold uppercase text-muted-foreground">Rantai: FS → Akun → Risiko → Prosedur → KKA → Bukti → Eksepsi → AJE → Kesimpulan</div>
        {d.chains.map((ch, ci) => (
          <div key={ci} className={`bg-white border rounded-sm p-2 ${ch.significant ? "border-red-300" : "border-border"}`} data-testid={`chain-${ci}`}>
            <div className="text-[11px] font-semibold mb-1">Risk {ch.risk} {ch.significant && <span className="text-[9px] text-red-600">SIGNIFIKAN</span>}</div>
            <div className="flex flex-wrap items-center gap-1">
              {ch.nodes.map((n, ni) => (
                <span key={n.id}>
                  <button onClick={() => setSel(n)} className={`text-[10px] px-1.5 py-0.5 border rounded-sm hover:ring-1 ${NODE_STYLE[n.status] || "bg-muted"}`} data-testid={`node-${n.id}`} title={n.label}>
                    <span className="opacity-60">{TYPE_LABEL[n.type] || n.type}:</span> {n.label.slice(0, 28)}
                  </button>
                  {ni < ch.nodes.length - 1 && <span className="text-muted-foreground mx-0.5">→</span>}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {gl && gl.chains.length > 0 && (
        <div className="space-y-2" data-testid="group-lineage">
          <div className="text-xs font-head font-semibold uppercase text-muted-foreground">Lineage Bukti Grup: LK Konsolidasian → Komponen → Angka (Goodwill/NCI/CTA) → Intercompany → Bukti Komponen</div>
          {gl.gaps.length > 0 && <div className="bg-red-50 border border-red-300 rounded-sm p-2 text-[11px] text-red-700" data-testid="group-lineage-gaps">LINEAGE GRUP GAP ({gl.gaps.length}): {gl.gaps.map((g) => g.ref).join(", ")} — bukti komponen belum tertaut.</div>}
          {gl.chains.map((ch, ci) => (
            <div key={ci} className="bg-white border border-border rounded-sm p-2" data-testid={`group-chain-${ci}`}>
              <div className="text-[11px] font-semibold mb-1">Komponen: {ch.entity}</div>
              <div className="flex flex-wrap items-center gap-1">
                {ch.nodes.map((n, ni) => (
                  <span key={n.id}>
                    <button onClick={() => setSel(n)} className={`text-[10px] px-1.5 py-0.5 border rounded-sm hover:ring-1 ${NODE_STYLE[n.status] || "bg-muted"}`} data-testid={`gnode-${n.id}`} title={n.label}>
                      <span className="opacity-60">{TYPE_LABEL[n.type] || n.type}:</span> {n.label.slice(0, 32)}
                    </button>
                    {ni < ch.nodes.length - 1 && <span className="text-muted-foreground mx-0.5">→</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {sel && (
        <div className="bg-white border border-slate-800 rounded-sm p-3 text-xs" data-testid="lineage-node-detail">
          <div className="flex items-center justify-between">
            <div className="font-head font-semibold">{TYPE_LABEL[sel.type] || sel.type}: {sel.label}</div>
            <button className="text-muted-foreground" onClick={() => setSel(null)}>Tutup</button>
          </div>
          <div className="text-muted-foreground mt-1">Status: {sel.status} · Ref: {sel.ref || "—"} · Record: {sel.record_id || "—"}</div>
          {sel.type === "snippet" && sel.record_id && sel.snip_version && <DocumentSnipPreview key={`${id}/${sel.record_id}/${sel.snip_version}`} snipId={sel.record_id} version={sel.snip_version} onUnavailable={e=>{setD(null);setGl(null);setSel(null);setError(getErrorMessage(e));}}/>}
          {sel.type === "evidence" && sel.record_id && <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]" onClick={() => downloadFile(`/evidence/${sel.record_id}/download`, sel.label)}><Download size={12} className="mr-1" />Unduh Bukti</Button>}
        </div>
      )}
    </div>
  );
}
